/**
 * お知らせの画面（issue #104・docs/01 9-7・15-1）。
 *
 * ここでできること:
 *  - お知らせを送る（宛先＝全社／拠点。どちらに送るかは**お知らせのトーク**を選んで決めます）
 *  - 送ったお知らせの一覧（本文の書き出し・**未読の人数**。お名前は出ません）
 *  - 「未読の方へ再通知」（残りの回数と、次に送れる時刻を出します）
 *  - お一人ずつの既読・未読の取り出し（本部管理者のみ。取り出した操作は履歴に残ります）
 *
 * 呼び方は決定30で「既読・未読」にそろえています（数えているのは「そのお知らせを開いたか」）。
 * 再通知のボタンの言葉と案内はサーバが返す文言（`notice` / `repush_label`）をそのまま出し、
 * 画面とサーバでずらしません。
 *
 * 一覧は黙って切りません（docs/06 決定22）。お知らせの部屋の選択肢は続きまで全部読み、
 * 出したお知らせは1回に50件ずつ・続きがあるときは「さらに表示する」を出します。
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, messageForStatus } from '../api/client.js';
import { nextStepAfter } from '../api/load-failure.js';
import { useApi } from '../api/context.js';
import { usePagedList } from '../api/paged-list.js';
import type {
  AnnouncementList,
  AnnouncementRecipients,
  AnnouncementSummary,
  ManagedChannel,
  ManagedChannelList,
  ReadReceiptList,
  RepushResult,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { channelSiteLabel } from '../channels/labels.js';
import { formatMonthDayTime } from '../format/datetime.js';
import { quietHoursNowSentence } from '../format/quiet-hours.js';
import { LoadMoreFooter } from '../components/LoadMoreFooter.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { can } from '../menu/visible.js';
import { endsWithRequest, sentence, withReason } from '../format/sentence.js';
import { formatTableDateTime } from '../format/datetime.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

/** お知らせの部屋の種類（サーバの設定の表と同じ名前）。 */
const ANNOUNCEMENT_TYPE = 'announcement';

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: string;
}

/**
 * 送る前に確かめる宛先の人数（issue #426）。確認の窓を開くたびに読み直す
 * （開いている間に名簿が変わっていても、送る直前の数を出すため）。
 */
type RecipientsState =
  | { status: 'loading' }
  | { status: 'ready'; counts: AnnouncementRecipients }
  | { status: 'failed' };

/** 宛先がいないときの言い方（サーバの断り文と同じ）。 */
const NO_RECIPIENTS_MESSAGE = 'このトークには、ほかにお知らせを受け取る方がいません。';

/** いま確認の窓に出している操作。 */
type Confirming =
  | { kind: 'none' }
  | { kind: 'publish' }
  | { kind: 'repush'; announcement: AnnouncementSummary }
  | { kind: 'export'; announcement: AnnouncementSummary };

export function AnnouncementsPage(): ReactNode {
  const api = useApi();
  // 頼み先に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const { profile } = useSession();
  const grant = profile?.grant;
  const canExport = grant ? can(grant, 'data.export') : false;

  const [rooms, setRooms] = useState<ManagedChannelList | null>(null);
  const [roomId, setRoomId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [roomsKey, setRoomsKey] = useState(0);

  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);
  const [confirming, setConfirming] = useState<Confirming>({ kind: 'none' });
  const [recipients, setRecipients] = useState<RecipientsState>({ status: 'loading' });
  const recipientsTicket = useRef(0);
  const [receipts, setReceipts] = useState<ReadReceiptList | null>(null);
  /**
   * 二重に出さないための鍵（docs/02 W2）。
   * 「出す」を押したときに1つ決め、**出せたと分かるまで同じ鍵を使い続けます**。
   * 応答だけが途中で失われて押し直した場合も、サーバは同じ鍵を見て、先に出た1件を返します
   * （新しいお知らせは作りません）。宛先か本文を変えたら、別のお知らせとして鍵を作り直します。
   */
  const pendingPublish = useRef<{ key: string; channelId: string; body: string } | null>(null);

  // お知らせを出せる部屋（全社・拠点）を読む。選択肢なので、続きまで全部読む（黙って切らない）
  useEffect(() => {
    let cancelled = false;
    setError(null);
    readAllRooms(api)
      .then((loaded) => {
        if (cancelled) return;
        setRooms(loaded);
        // 初めの宛先は全社のお知らせ（拠点の付かないもの）。無ければ一覧の先頭（#621）
        const first = loaded.channels.find((room) => room.site_name === null) ?? loaded.channels[0];
        setRoomId((current) => current || (first?.id ?? ''));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // お知らせの部屋の一覧は、部屋を管理できる方だけが読めます。
        // 断られた理由が「お知らせ」の画面では分かりにくいので、言い換えてお伝えします
        setError(
          caught instanceof ApiError && caught.status === 403
            ? `お知らせを送れるトークの一覧を読むには、トークを管理する権限も必要です。${wording.ask(ROLE_REQUIREMENTS.channelManage)}`
            : toMessage(caught),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [api, roomsKey, wording]);

  // 選んだ部屋に出したお知らせの一覧（人数つき・部屋を選び直したら最初から読み直す）
  const list = usePagedList<AnnouncementSummary>(
    roomId,
    async (cursor) => {
      if (roomId === '') return { items: [], next: null };
      const loaded = await api.get<AnnouncementList>('/announcements', {
        query: { channel_id: roomId, ...(cursor !== null ? { cursor } : {}) },
      });
      return { items: loaded.announcements, next: loaded.next_cursor ?? null };
    },
    (announcement) => announcement.id,
  );
  const announcements = list.items;

  async function publish(): Promise<void> {
    // 送る前に確かめた宛先（いまお使いの方が0名か）で、送ったあとの言い方を決める
    const waitingOnly = onlyWaiting(recipients);
    // 夜間に通知音を鳴らさない時間帯の中なら、音は鳴らないとお伝えする（issue #434）
    const quietNow = quietHoursNowOf(recipients);
    setBusy(true);
    setResult(null);
    const trimmed = body.trim();
    const pending = pendingPublish.current;
    const key =
      pending !== null && pending.channelId === roomId && pending.body === trimmed
        ? pending.key
        : crypto.randomUUID();
    pendingPublish.current = { key, channelId: roomId, body: trimmed };
    try {
      await api.post('/announcements', {
        body: {
          channel_id: roomId,
          // 同じお知らせが二重に出ないようにするための鍵（押し直しても1件のまま）
          client_msg_id: key,
          body: trimmed,
        },
      });
      // 出せたので、この鍵の役目は終わり（次のお知らせは新しい鍵で出す）
      pendingPublish.current = null;
      setConfirming({ kind: 'none' });
      setBody('');
      setResult(
        waitingOnly
          ? {
              // いまお使いの方が0名のときは「通知もお送りしています」と言わない（統合レビュー・#426）
              tone: 'success',
              message: 'お知らせを送りました。アプリを使い始めた方から読めます。',
              nextStep: '使い始めた方の人数は、下の一覧の「宛先」に出ます。',
            }
          : {
              tone: 'success',
              message:
                quietNow === null
                  ? 'お知らせを送りました。通知もお送りしています。'
                  : `お知らせを送りました。通知もお送りしています。${quietHoursNowSentence(quietNow)}`,
              nextStep: '下の一覧で、未読の人数をご確認いただけます。',
            },
      );
      // 読み足した分と表示の位置を残したまま、いま出ている件数ぶんを読み直す（issue #424）
      void list.refresh();
    } catch (caught: unknown) {
      setConfirming({ kind: 'none' });
      const reason = toMessage(caught);
      setResult({
        tone: 'error',
        message: withReason(`お知らせを送れませんでした`, reason),
        // 理由の文が次にすることで終わっているとき（休眠のトーク・#459 など）は、決まり文句を足さない
        ...(endsWithRequest(reason)
          ? {}
          : { nextStep: '本文をご確認のうえ、もう一度お試しください。' }),
      });
    } finally {
      setBusy(false);
    }
  }

  /** 送る前の確認の窓を開き、宛先の人数を読む（issue #426）。 */
  function confirmPublish(): void {
    setRecipients({ status: 'loading' });
    setConfirming({ kind: 'publish' });
    // 開き直したときに、前に開いたときの答えが後から届いて上書きしないよう、回ごとに印を付ける
    const ticket = ++recipientsTicket.current;
    api
      .get<AnnouncementRecipients>('/announcements/recipients', {
        query: { channel_id: roomId },
      })
      .then((counts) => {
        if (ticket === recipientsTicket.current) setRecipients({ status: 'ready', counts });
      })
      .catch(() => {
        if (ticket === recipientsTicket.current) setRecipients({ status: 'failed' });
      });
  }

  async function repush(announcement: AnnouncementSummary): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const done = await api.post<RepushResult>(`/announcements/${announcement.id}/repush`);
      setConfirming({ kind: 'none' });
      setResult({ tone: 'success', message: done.notice });
      void list.refresh();
    } catch (caught: unknown) {
      setConfirming({ kind: 'none' });
      setResult({
        tone: 'error',
        message: withReason(`再通知できませんでした`, toMessage(caught)),
        nextStep: '回数の上限と、次に送れる時刻をご確認ください。',
      });
      // 一覧を開いている間にお知らせが取り消されていた場合は、読み直すと一覧から外れる
      void list.refresh();
    } finally {
      setBusy(false);
    }
  }

  async function exportReceipts(announcement: AnnouncementSummary): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const loaded = await api.get<ReadReceiptList>(
        `/announcements/${announcement.id}/read-receipts`,
      );
      setConfirming({ kind: 'none' });
      setReceipts(loaded);
      setResult({
        tone: 'success',
        message: 'お一人ずつの既読・未読を取り出しました。この操作は操作履歴に残ります。',
      });
    } catch (caught: unknown) {
      setConfirming({ kind: 'none' });
      setResult({
        tone: 'error',
        message: withReason(`取り出せませんでした`, toMessage(caught)),
        // 断った理由（…の方だけです）はサーバの文に入っている。権限で断られたときだけ頼み先を
        // その会社の呼び名で案内し、通信の失敗などでは時間をおいていただく（issue #480）
        nextStep:
          caught instanceof ApiError && caught.status === 403
            ? wording.ask(ROLE_REQUIREMENTS.exportReadReceipts)
            : nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusy(false);
    }
  }

  const selectedRoom = rooms?.channels.find((room) => room.id === roomId) ?? null;

  return (
    <article>
      <PageHeader
        title="お知らせ"
        description="全社・拠点へお知らせを送り、未読の人数を確かめて、未読の方へ通知を送り直します。"
      />

      {error ? <ErrorBlock message={error} onRetry={() => setRoomsKey((n) => n + 1)} /> : null}

      {result ? (
        <ResultBanner
          reveal
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {rooms && rooms.channels.length === 0 ? (
        <EmptyBlock label="お知らせを送れるトークがありません。先に「トーク」の画面で、お知らせのトークを作ってください。" />
      ) : null}

      {rooms && rooms.channels.length > 0 ? (
        <FormCard
          title="お知らせを送る"
          description="宛先は、お知らせのトークで決まります（全社のトークなら全社へ、拠点のトークはその拠点の方へ届きます）。"
          submitLabel="この内容で送る"
          submitBusyLabel="送っています…"
          busy={busy && confirming.kind === 'publish'}
          onSubmit={() => {
            if (body.trim() === '') {
              setResult({ tone: 'error', message: 'お知らせの本文を入力してください。' });
              return;
            }
            confirmPublish();
          }}
        >
          <label className="ui-field" htmlFor="announcement-room">
            <span>宛先（お知らせのトーク）</span>
            <select
              id="announcement-room"
              value={roomId}
              onChange={(event) => setRoomId(event.target.value)}
            >
              {rooms.channels.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}（{channelSiteLabel(room.site_name)}）
                </option>
              ))}
            </select>
          </label>

          <label className="ui-field" htmlFor="announcement-body">
            <span>本文</span>
            <textarea
              id="announcement-body"
              rows={5}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </label>
        </FormCard>
      ) : null}

      {roomId !== '' ? (
        <Card
          title="送ったお知らせ"
          description={
            selectedRoom
              ? `${selectedRoom.name}（${channelSiteLabel(selectedRoom.site_name)}）に送ったお知らせです。`
              : undefined
          }
        >
          {list.error !== null ? <ErrorBlock failure={list.error} onRetry={list.reload} /> : null}
          {list.error === null && announcements === null ? (
            <LoadingBlock label="お知らせを読み込んでいます…" />
          ) : null}
          {announcements && announcements.length === 0 ? (
            <EmptyBlock label="このトークに出ているお知らせはありません（送信取消したお知らせは出ません）。" />
          ) : null}
          {announcements && announcements.length > 0 ? (
            <DataTable label="送ったお知らせの一覧" minWidth="46rem">
              <thead>
                <tr>
                  <th scope="col">内容</th>
                  <th scope="col">送った日時</th>
                  <th scope="col">宛先</th>
                  <th scope="col">未読</th>
                  <th scope="col">再通知</th>
                  <th scope="col">
                    <span className="admin-visually-hidden">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <th scope="row" className="ui-cell-wrap">
                      {announcement.body_excerpt === undefined ? (
                        '―'
                      ) : announcement.body_excerpt === null ? (
                        <small>参加していないトークのため、本文は表示していません</small>
                      ) : (
                        announcement.body_excerpt
                      )}
                    </th>
                    <td className="ui-cell-date">{formatTableDateTime(announcement.created_at)}</td>
                    <td className="ui-cell-num">{announcement.total}名</td>
                    <td className="ui-cell-num">{announcement.unread_count}名</td>
                    <td>
                      {announcement.repush_count}／{announcement.max_repush_count}回
                      {announcement.last_repush_at !== null ? (
                        <small>前回 {formatTableDateTime(announcement.last_repush_at)}</small>
                      ) : null}
                    </td>
                    <td className="ui-cell-wrap">
                      <div className="admin-row-actions">
                        {announcement.unsent === true ? null : (
                          <Button
                            variant="secondary"
                            compact
                            disabled={!announcement.can_repush}
                            onClick={() => setConfirming({ kind: 'repush', announcement })}
                          >
                            {announcement.repush_label}
                          </Button>
                        )}
                        {canExport ? (
                          <Button
                            variant="quiet"
                            compact
                            onClick={() => setConfirming({ kind: 'export', announcement })}
                          >
                            お一人ずつの既読・未読
                          </Button>
                        ) : null}
                      </div>
                      {!announcement.can_repush && announcement.cannot_repush_reason !== null ? (
                        <small className="admin-note">
                          {sentence(announcement.cannot_repush_reason)}
                          {announcement.next_repush_available_at !== null
                            ? `次に再通知できるのは${formatMonthDayTime(announcement.next_repush_available_at)}からです。`
                            : ''}
                        </small>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
          <LoadMoreFooter list={list} />
        </Card>
      ) : null}

      {receipts ? (
        <Card
          title="お一人ずつの既読・未読"
          description="取り出した操作は操作履歴に残ります。画面を離れると消えます。"
        >
          <DataTable label="お一人ずつの既読・未読">
            <thead>
              <tr>
                <th scope="col">お名前</th>
                <th scope="col">既読・未読</th>
                <th scope="col">読んだ日時</th>
              </tr>
            </thead>
            <tbody>
              {receipts.read_receipts.map((row) => (
                <tr key={row.user_id}>
                  <th scope="row">{row.display_name}</th>
                  <td>{row.has_read ? '既読' : '未読'}</td>
                  <td className="ui-cell-date">{formatTableDateTime(row.read_at)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirming.kind === 'publish'}
        title={hasNoRecipients(recipients) ? 'このトークには送れません' : 'このお知らせを送ります'}
        message={
          hasNoRecipients(recipients) ? (
            // 宛先がいないときは「届きます」と言わない（送れない理由と、次にすることだけ）
            <>
              {NO_RECIPIENTS_MESSAGE}
              <br />
              宛先のトークを選び直してください。
            </>
          ) : onlyWaiting(recipients) ? (
            // いまお使いの方が0名（まだ使い始めていない方だけ）のときは「いますぐ届きます」と言わない。
            // 通知はまだ誰にも届かず、使い始めた方から読める（統合レビュー・issue #426）
            <>
              {selectedRoom
                ? `「${selectedRoom.name}」（${channelSiteLabel(selectedRoom.site_name)}）に出します。`
                : 'お知らせを出します。'}
              いまアプリをお使いの方はいないため、通知はまだ届きません。
              <br />
              {recipientsLine(recipients)}
            </>
          ) : (
            <>
              {selectedRoom
                ? `「${selectedRoom.name}」（${channelSiteLabel(selectedRoom.site_name)}）の方へ、いますぐ届きます。`
                : 'お知らせを出します。'}
              {quietHoursNowOf(recipients) !== null ? (
                // 夜間は届くが通知音は鳴らない（issue #434）。「いますぐ届きます」だけだと鳴ると思わせる
                <>
                  <br />
                  {quietHoursNowSentence(quietHoursNowOf(recipients)!)}
                </>
              ) : null}
              <br />
              {recipientsLine(recipients)}
            </>
          )
        }
        reversibility={
          hasNoRecipients(recipients)
            ? 'まだ何も送っていません。'
            : '送ったお知らせは、この画面からは取り消せません。送ったことは操作履歴に残ります。'
        }
        reversible={hasNoRecipients(recipients)}
        tone="primary"
        confirmLabel="送る"
        busy={busy}
        // 宛先の人数を確かめている間と、宛先が0名のときは送れない（issue #426）
        confirmDisabled={recipients.status === 'loading' || hasNoRecipients(recipients)}
        onConfirm={() => void publish()}
        onCancel={() => setConfirming({ kind: 'none' })}
      />

      <ConfirmDialog
        open={confirming.kind === 'repush'}
        title="未読の方へ、通知を送り直します"
        message={
          confirming.kind === 'repush'
            ? `未読の${confirming.announcement.unread_count}名へ送り直します（再通知は${confirming.announcement.max_repush_count}回までです）。`
            : ''
        }
        reversibility="送り直した記録は操作履歴に残ります。端末の設定によっては届かないことがあります。"
        tone="primary"
        confirmLabel="送り直す"
        busy={busy}
        onConfirm={() => {
          if (confirming.kind === 'repush') void repush(confirming.announcement);
        }}
        onCancel={() => setConfirming({ kind: 'none' })}
      />

      <ConfirmDialog
        open={confirming.kind === 'export'}
        title="お一人ずつの既読・未読を取り出します"
        message="どなたが読んだかが、お名前つきで画面に出ます。"
        reversibility="取り出した操作は、誰がいつ行ったかが操作履歴に残ります。"
        tone="primary"
        confirmLabel="取り出す"
        busy={busy}
        onConfirm={() => {
          if (confirming.kind === 'export') void exportReceipts(confirming.announcement);
        }}
        onCancel={() => setConfirming({ kind: 'none' })}
      />
    </article>
  );
}

/** 宛先がいない（0名）と分かっているか。 */
function hasNoRecipients(state: RecipientsState): boolean {
  return (
    state.status === 'ready' && state.counts.recipient_count + state.counts.waiting_count === 0
  );
}

/** いまお使いの方は0名で、まだアプリを使い始めていない方だけがいると分かっているか。 */
function onlyWaiting(state: RecipientsState): boolean {
  return (
    state.status === 'ready' && state.counts.recipient_count === 0 && state.counts.waiting_count > 0
  );
}

/** いまが夜間に通知音を鳴らさない時間帯の中なら、その時間帯（分からない・外なら null）。 */
function quietHoursNowOf(state: RecipientsState): { start: string; end: string } | null {
  return state.status === 'ready' ? (state.counts.quiet_hours_now ?? null) : null;
}

/** 確認の窓に出す宛先の人数の1文（issue #426）。 */
function recipientsLine(state: RecipientsState): string {
  if (state.status === 'loading') return '宛先の人数を確かめています…';
  if (state.status === 'failed') {
    return '宛先の人数を確かめられませんでした。送ると、宛先がいないときはその旨をお知らせします。';
  }
  const { recipient_count: count, waiting_count: waiting } = state.counts;
  if (count === 0 && waiting > 0) {
    return `まだアプリを使い始めていない方が${waiting}名います（使い始めると読めます）。`;
  }
  return waiting > 0
    ? `宛先 ${count}名（ほかに、まだアプリを使い始めていない方が${waiting}名います。使い始めると読めます）`
    : `宛先 ${count}名`;
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}

/** お知らせの部屋を、続きまで全部読む（選択肢なので途中で切らない）。 */
async function readAllRooms(api: ReturnType<typeof useApi>): Promise<ManagedChannelList> {
  const channels: ManagedChannel[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  // 1回500件まで。続きの位置は毎回必ず後ろへ進むので、読み切れば終わる。
  // 見ている間にメッセージがあったトークは一覧の下にもう一度届くことがあるので、
  // 同じトークは1回だけ並べる（issue #293）
  for (;;) {
    const page: ManagedChannelList = await api.get<ManagedChannelList>('/admin/channels', {
      query: { type: ANNOUNCEMENT_TYPE, limit: '500', ...(cursor !== null ? { cursor } : {}) },
    });
    for (const channel of page.channels) {
      if (seen.has(channel.id)) continue;
      seen.add(channel.id);
      channels.push(channel);
    }
    cursor = page.next_cursor ?? null;
    if (cursor === null) return { channels, next_cursor: null };
  }
}
